import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { enablePush, disablePush, getPushStatus, PushStatus } from '../../lib/push';
import { usePwaInstall } from '../../hooks/usePwaInstall';
import { DevicePhoneMobileIcon, BellAlertIcon, ArrowDownTrayIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

/** Push értesítés + alkalmazás telepítése kártya (Beállítások → Értesítések). */
const PushSettingsCard: React.FC = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState<PushStatus>('default');
  const [busy, setBusy] = useState(false);
  const { canInstall, install, isInstalled, isIos } = usePwaInstall();

  const refresh = async () => setStatus(await getPushStatus());
  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (status === 'enabled') { await disablePush(); toast.success('Push kikapcsolva ezen az eszközön'); }
      else {
        const r = await enablePush((user as any)?.organizationId);
        if (r.ok) toast.success('Push értesítések bekapcsolva ezen az eszközön'); else toast.error(r.reason || 'Nem sikerült');
      }
    } finally { setBusy(false); refresh(); }
  };

  const statusText: Record<PushStatus, string> = {
    unsupported: isIos ? 'iPhone/iPad: előbb tedd a kezdőképernyőre (Megosztás → Kezdőképernyőhöz adás), és a telepített appból kapcsold be.' : 'Ez a böngésző nem támogatja a push értesítést.',
    'no-vapid': 'Hiányzik a VAPID kulcs a buildből (REACT_APP_FIREBASE_VAPID_KEY).',
    denied: 'A böngésző tiltja az értesítéseket – a címsor lakat ikonjánál engedélyezhető, utána kapcsold be itt.',
    default: 'Zárt böngésző / zárt app mellett is megkapod az értesítéseket ezen az eszközön.',
    enabled: 'Bekapcsolva ezen az eszközön.',
    disabled: 'Kikapcsolva ezen az eszközön.'
  };

  return (
    <div className="crm-card">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
        <BellAlertIcon className="w-4 h-4 text-gray-400" />
        <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Push értesítések és alkalmazás</h3>
      </div>
      <div className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              Push értesítés ezen az eszközön
              {status === 'enabled' && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 h-5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"><CheckCircleIcon className="w-3 h-3" />aktív</span>}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{statusText[status]}</p>
          </div>
          <button type="button" onClick={toggle} disabled={busy || status === 'unsupported' || status === 'no-vapid' || status === 'denied'} className={`crm-btn h-8 text-xs flex-shrink-0 ${status === 'enabled' ? 'crm-btn-secondary' : 'crm-btn-primary'}`}>
            {busy ? '…' : status === 'enabled' ? 'Kikapcsolás' : 'Bekapcsolás'}
          </button>
        </div>

        <div className="flex items-start justify-between gap-3 border-t border-[var(--crm-border)] pt-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100"><DevicePhoneMobileIcon className="w-4 h-4 text-gray-400" />Alkalmazásként telepítés</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {isInstalled ? 'Telepítve – a kezdőképernyőről / asztalról indítható, offline is megnyílik.'
                : isIos ? 'iPhone/iPad: Safari → Megosztás → „Kezdőképernyőhöz adás”.'
                : canInstall ? 'Saját ikon a kezdőképernyőn / asztalon, teljes képernyő, offline indulás.'
                : 'A böngésző menüjéből telepíthető („Alkalmazás telepítése” / „Add to Home screen”).'}
            </p>
          </div>
          {canInstall && !isInstalled && (
            <button type="button" onClick={install} className="crm-btn crm-btn-secondary h-8 text-xs flex-shrink-0"><ArrowDownTrayIcon className="w-4 h-4" />Telepítés</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PushSettingsCard;
